<script setup lang="ts">
// -------------------------------
// 🔹 Imports Vue & composables
// -------------------------------
import { ref } from 'vue';

// -------------------------------
// 🔹 Composants
// -------------------------------

import BaseProduct from '@/components/common/BaseProduct.vue';
import BaseButtonPrimary from '@/components/common/BaseButtonPrimary.vue';

// -------------------------------
// 🔹 Stores
// -------------------------------
import { useDialogStore } from '@/store/dialog.store';

// -------------------------------
// 🔹 Utils & refs
// -------------------------------
import { getDateFormat, getDateHourMinute } from '@/utils/formatDate';
import { formatPrice } from '@/utils/formatPrice';
import { isNextStep_dialog } from '@/refs/dashboard.refs';

// -------------------------------
// 🔹 Stores instanciés
// -------------------------------
const dialogStore = useDialogStore();
// -------------------------------
// 🔹 Gestion du dialogue
// -------------------------------

/**
 * Passe à l’étape suivante dans le dialogue (stepper).
 */
const handleNextStep = (): void => {
  isNextStep_dialog.value = !isNextStep_dialog.value;
};

/**
 * Ferme le dialogue et réinitialise les valeurs associées.
 */
const handleClose = (): void => {
  dialogStore.closeDialog();
};
</script>

<template>
  <div>
    <div>
      <div class="w-full space-y-2" v-if="!isNextStep_dialog">
        <div class="flex w-full flex-col justify-between gap-1">
          <div>
            <p
              class="text-grand-text text-14px font-tt-semiBold-Normal leading-normal"
            >
              Information du réstaurant
            </p>
          </div>

          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Nom du réstaurant
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.restaurant?.designation }}
            </p>
          </div>
          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Adresse
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.restaurant?.address }}
            </p>
          </div>

          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Code monnaie convertie
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.currencyConvertedCode }}
            </p>
          </div>
        </div>

        <div class="flex w-full flex-col justify-between gap-1">
          <div>
            <p
              class="text-grand-text text-14px font-tt-semiBold-Normal leading-normal"
            >
              Information supplementaire sur le paiement
            </p>
          </div>

          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Montant du paiement
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{
                formatPrice(dialogStore.content?.items?.payments?.[0]?.amount)
              }}
            </p>
          </div>
          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Date d’expiration de la carte
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content?.items?.payments?.[0]?.cardExpire }}
            </p>
          </div>

          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Réseau de paiement
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content?.items?.payments?.[0]?.network }}
            </p>
          </div>

          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Processeur de paiement
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.payments?.[0]?.processor }}
            </p>
          </div>
          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Identifiant de session
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.payments?.[0]?.sid }}
            </p>
          </div>

          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              4 derniers chiffres de la carte
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.payments?.[0]?.cardLastDigit }}
            </p>
          </div>

          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Montant net
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ formatPrice(dialogStore.content.items?.payments?.[0]?.net) }}
            </p>
          </div>
        </div>
        <div class="flex w-full flex-row items-center justify-between">
          <p
            class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
          >
            Date de création de la commande
          </p>
          <p class="text-14px text-grand-text font-tt-medium-Normal">
            {{ getDateFormat(dialogStore.content?.items?.createdAt) }} à
            {{ getDateHourMinute(dialogStore.content?.items?.createdAt) }}
          </p>
        </div>
        <div class="flex w-full flex-row items-center justify-between">
          <p
            class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
          >
            Date de mis à jour de la commande
          </p>
          <p class="text-14px text-grand-text font-tt-medium-Normal">
            {{ getDateFormat(dialogStore.content.items?.updatedAt) }} à
            {{ getDateHourMinute(dialogStore.content.items?.updatedAt) }}
          </p>
        </div>

        <div class="flex w-full flex-col justify-between gap-1">
          <div>
            <p
              class="text-grand-text text-14px font-tt-semiBold-Normal leading-normal"
            >
              Conversion de devise
            </p>
          </div>

          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Nom du devise
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.currencyConverted?.name }}
            </p>
          </div>
          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Description
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.currencyConverted?.description }}
            </p>
          </div>

          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Code monnaie convertie
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.currencyConvertedCode }}
            </p>
          </div>
        </div>

        <div class="flex w-full flex-col justify-between gap-1">
          <div>
            <p
              class="text-grand-text text-14px font-tt-semiBold-Normal leading-normal"
            >
              Devise avant conversion
            </p>
          </div>

          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Nom du devise
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.currencyOriginal?.name }}
            </p>
          </div>
          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Description
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.currencyOriginal?.description }}
            </p>
          </div>

          <div class="flex w-full flex-row items-center justify-between">
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              Code monnaie d’origine
            </p>
            <p
              class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
            >
              {{ dialogStore.content.items?.currencyOriginalCode }}
            </p>
          </div>
        </div>

        <div class="flex w-full flex-row items-center justify-between">
          <p
            class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
          >
            Taxe sur la valeur ajoutée
          </p>
          <p class="text-14px text-grand-text font-tt-medium-Normal">
            {{ dialogStore.content.items?.exchangeRate }}
          </p>
        </div>
        <div class="flex w-full flex-row items-center justify-between">
          <p
            class="text-grand-text text-14px font-tt-medium-Normal leading-normal"
          >
            Taux de conversion
          </p>
          <p class="text-14px text-grand-text font-tt-medium-Normal">
            {{ formatPrice(dialogStore.content.items?.taxAmount) }}
          </p>
        </div>
      </div>

      <div v-else>
        <div class="flex flex-row flex-wrap gap-2">
          <div v-for="item in dialogStore.content.items?.items">
            <BaseProduct
              :promotionTitle="'Personnalisable'"
              :promotionValue="item?.discount + '%'"
              :promotionDesc="'Offert'"
              :price="item?.unityPrice"
              :categorie="item?.product?.categories?.[0]?.category?.designation"
              :designation="item?.product?.designation"
              :descritpion="item?.product?.description"
              :img="item?.product?.image?.info?.url"
              :alt="item?.product?.image?.designation"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="invisible h-[90px] w-full bg-amber-600" />
    <div class="fixed bottom-0 left-0 w-full bg-white py-6">
      <div class="flex flex-row items-center justify-between gap-8 px-[16px]">
        <div class="w-full" @click="handleClose">
          <BaseButtonPrimary
            type="button"
            :label="isNextStep_dialog ? 'Fermer' : 'Fermer'"
            style_of_btn="primary_color_of_btn "
            class="w-full text-white"
          />
        </div>

        <div class="w-full" @click="handleNextStep">
          <BaseButtonPrimary
            type="button"
            :label="isNextStep_dialog ? 'Précédent' : 'Voir Plus'"
            style_of_btn="secondary_color_of_btn "
            class="text-grand-text w-full"
          />
        </div>
      </div>
    </div>
  </div>
</template>
